import { useCallback, useEffect, useState } from "react";

/** The app's calls to `…/notifications/push…` (P11-05), so the console and the customer app share this. */
export interface PushApi {
  /** The VAPID public key, or null when push isn't set up. */
  publicKey: () => Promise<string | null>;
  subscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) => Promise<unknown>;
  unsubscribe: (endpoint: string) => Promise<unknown>;
}

/**
 * - `unsupported`: this browser has no push (an iPhone outside the installed app);
 * - `unavailable`: the platform has no push keys;
 * - `blocked`: the person said no, and only the browser's settings can undo it.
 */
export type PushStatus = "checking" | "unsupported" | "unavailable" | "blocked" | "off" | "on";

export const pushSupported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** The base64url VAPID key as the bytes PushManager.subscribe takes. */
export function keyBytes(base64url: string) {
  const base64 = base64url
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(base64url.length / 4) * 4, "=");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

const asBody = (subscription: PushSubscription) => {
  const json = subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
};

const current = async () =>
  (await (await navigator.serviceWorker.getRegistration())?.pushManager.getSubscription()) ?? null;

/**
 * Push on this device (P11-05). Permission is asked only from `turnOn`, which the person starts
 * by tapping; nothing asks on load. A browser already subscribed is told to the API again on
 * load, so the switch and the server agree for whoever is signed in now.
 * `worker` is the service worker that shows the notifications (`/sw.js`, `/push-sw.js`).
 */
export function usePush(api: PushApi, worker: string) {
  const [status, setStatus] = useState<PushStatus>("checking");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: checked once, on load
  useEffect(() => {
    let live = true;
    const set = (next: PushStatus) => live && setStatus(next);
    (async () => {
      if (!pushSupported()) return set("unsupported");
      if (!(await api.publicKey())) return set("unavailable");
      if (Notification.permission === "denied") return set("blocked");
      const subscription = Notification.permission === "granted" ? await current() : null;
      if (!subscription) return set("off");
      await api.subscribe(asBody(subscription));
      set("on");
    })().catch(() => set("off"));
    return () => {
      live = false;
    };
  }, []);

  const turnOn = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const key = await api.publicKey();
      if (!key) return setStatus("unavailable");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return setStatus(permission === "denied" ? "blocked" : "off");
      await navigator.serviceWorker.register(worker);
      const registration = await navigator.serviceWorker.ready;
      // a fresh subscription, so one made with an old key can't get in the way
      await (await registration.pushManager.getSubscription())?.unsubscribe();
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key),
      });
      await api.subscribe(asBody(subscription));
      setStatus("on");
    } catch {
      setError("Notifications couldn't be turned on. Try again.");
    } finally {
      setBusy(false);
    }
  }, [api, worker]);

  const turnOff = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await forgetPush(api, { strict: true });
      setStatus("off");
    } catch {
      setError("Notifications couldn't be turned off. Try again.");
    } finally {
      setBusy(false);
    }
  }, [api]);

  return { status, busy, error, turnOn, turnOff };
}

/**
 * Stops push on this browser: the API first (while still signed in), then the browser. Signing
 * out calls it, so the next person on a shared phone doesn't see the last one's messages; there
 * it never fails (`strict` off).
 */
export async function forgetPush(api: PushApi, { strict = false } = {}) {
  try {
    if (!pushSupported()) return;
    const subscription = await current();
    if (!subscription) return;
    await api.unsubscribe(subscription.endpoint);
    await subscription.unsubscribe();
  } catch (error) {
    if (strict) throw error;
  }
}
